import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

// Download URLs are intentionally public — the agent installer
// contains the Fleet enroll secret baked in at build time.
// Without a valid enroll secret the agent cannot connect.
// Never display the enroll secret or Fleet URL in the UI.

type AgentPlatform = 'windows' | 'macos' | 'linux';

type DetectedOS = AgentPlatform | 'unknown';

const WINDOWS_HREF = 'https://downloads.lognsec.com/shieldsme-agent-windows.msi';
const MACOS_HREF = 'https://downloads.lognsec.com/shieldsme-agent-macos.pkg';
const LINUX_HREF = 'https://downloads.lognsec.com/shieldsme-agent-linux.deb';

const INSTALL_STEPS: Record<
  AgentPlatform,
  { title: string; lines: string[] }
> = {
  windows: {
    title: 'Windows',
    lines: [
      'Run the downloaded .msi installer',
      'Follow the installation wizard — accept defaults',
      'The agent starts automatically after installation',
      'Your device appears in ShieldSME within 2 minutes',
    ],
  },
  macos: {
    title: 'macOS',
    lines: [
      'Open the downloaded .pkg file',
      'Follow the installation prompts',
      'Allow the system extension when prompted in Security settings',
      'Your device appears in ShieldSME within 2 minutes',
    ],
  },
  linux: {
    title: 'Linux',
    lines: [
      'Open terminal in your downloads folder',
      'Run: sudo dpkg -i shieldsme-agent-linux.deb',
      'The agent starts automatically after installation',
      'Your device appears in ShieldSME within 2 minutes',
    ],
  },
};

export default function DownloadPage() {
  const detectedOS = useMemo((): DetectedOS => {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    if (/Windows/i.test(ua)) return 'windows';
    if (/Mac/i.test(ua)) return 'macos';
    if (/Linux/i.test(ua)) return 'linux';
    return 'unknown';
  }, []);

  const defaultStepsPlatform: AgentPlatform = detectedOS === 'unknown' ? 'windows' : detectedOS;
  const [instructionPlatform, setInstructionPlatform] = useState<AgentPlatform>(defaultStepsPlatform);

  useEffect(() => {
    if (detectedOS !== 'unknown') setInstructionPlatform(detectedOS);
  }, [detectedOS]);

  const cards: Array<{
    id: AgentPlatform;
    icon: string;
    title: string;
    description: string;
    size: string;
    button: string;
    href: string;
  }> = [
    {
      id: 'windows',
      icon: '💻',
      title: 'Windows',
      description: 'Windows 10 and above · 64-bit',
      size: '27 MB',
      button: 'Download for Windows →',
      href: WINDOWS_HREF,
    },
    {
      id: 'macos',
      icon: '🍎',
      title: 'macOS',
      description: 'macOS 11 Big Sur and above',
      size: '58 MB',
      button: 'Download for macOS →',
      href: MACOS_HREF,
    },
    {
      id: 'linux',
      icon: '🐧',
      title: 'Linux',
      description: 'Ubuntu, Debian and compatible · 64-bit',
      size: '56 MB',
      button: 'Download for Linux →',
      href: LINUX_HREF,
    },
  ];

  const steps = INSTALL_STEPS[instructionPlatform];

  return (
    <div className="mx-auto max-w-4xl space-y-10 pb-8">
      <div
        className="rounded-xl border border-accent/40 bg-accent/10 px-4 py-3 text-sm font-medium text-accent"
        role="status"
      >
        🛡️ Your first device is monitored free for 30 days — no payment needed
      </div>

      <header className="space-y-2">
        <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">Protect This Device</h1>
        <p className="max-w-2xl text-sm text-muted sm:text-base">
          Install the ShieldSME security agent to monitor this device for threats in real time. Takes less than 2
          minutes.
        </p>
      </header>

      <section aria-labelledby="download-cards-heading">
        <h2 id="download-cards-heading" className="sr-only">
          Choose your platform
        </h2>
        <div className="grid gap-4 sm:grid-cols-3">
          {cards.map((c) => {
            const recommended = detectedOS === c.id;
            return (
              <div
                key={c.id}
                className={`flex flex-col rounded-2xl border bg-surface/60 p-5 transition ${
                  recommended ? 'border-accent shadow-lg shadow-accent/10 ring-1 ring-accent/20' : 'border-border'
                }`}
              >
                {recommended ? (
                  <span className="mb-2 inline-flex w-fit rounded-full bg-accent/15 px-2.5 py-0.5 text-xs font-semibold text-accent">
                    Recommended for your device
                  </span>
                ) : null}
                <span className="text-3xl" aria-hidden>
                  {c.icon}
                </span>
                <h3 className="mt-2 text-lg font-semibold text-foreground">{c.title}</h3>
                <p className="mt-1 flex-1 text-sm text-muted">{c.description}</p>
                <p className="mt-3 text-xs font-medium text-muted">File size: {c.size}</p>
                <a
                  href={c.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setInstructionPlatform(c.id)}
                  className="mt-4 inline-flex w-full items-center justify-center rounded-lg border border-accent/40 bg-accent/10 py-2.5 text-sm font-semibold text-accent transition hover:bg-accent/20"
                >
                  {c.button}
                </a>
              </div>
            );
          })}
        </div>
      </section>

      <section className="rounded-2xl border border-border bg-surface/60 p-5 sm:p-6" aria-labelledby="install-steps">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 id="install-steps" className="text-lg font-semibold text-foreground">
            Installation instructions
          </h2>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(INSTALL_STEPS) as AgentPlatform[]).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setInstructionPlatform(p)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                  instructionPlatform === p
                    ? 'border border-accent/40 bg-accent/15 text-accent'
                    : 'border border-border bg-surface text-muted hover:border-accent/40 hover:text-foreground'
                }`}
              >
                {INSTALL_STEPS[p].title}
              </button>
            ))}
          </div>
        </div>
        <p className="mt-2 text-xs text-muted">
          Showing steps for <span className="font-medium text-foreground">{steps.title}</span>. Select another platform
          above to switch.
        </p>
        <ol className="mt-6 space-y-4">
          {steps.lines.map((line, i) => (
            <li key={i} className="flex gap-3 text-sm text-muted">
              <span
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent/15 text-xs font-bold text-accent"
                aria-hidden
              >
                {i + 1}
              </span>
              <span className="pt-0.5 leading-relaxed text-foreground">{line}</span>
            </li>
          ))}
        </ol>
      </section>

      <section aria-labelledby="monitor-headline">
        <h2 id="monitor-headline" className="text-lg font-semibold text-foreground">
          What we monitor on this device
        </h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {[
            {
              icon: '🔍',
              title: 'Running Processes',
              body: 'Detect malware, cryptominers, and suspicious software',
            },
            {
              icon: '🌐',
              title: 'Network Connections',
              body: 'Spot unusual outbound traffic and suspicious connections',
            },
            {
              icon: '📊',
              title: 'System Health',
              body: 'Monitor CPU, memory, and disk for anomalies',
            },
            {
              icon: '🔒',
              title: 'Security Events',
              body: 'Track login attempts and privilege escalation',
            },
          ].map((item) => (
            <div key={item.title} className="rounded-xl border border-border bg-surface/50 p-4">
              <p className="text-lg" aria-hidden>
                {item.icon}
              </p>
              <h3 className="mt-2 font-semibold text-foreground">{item.title}</h3>
              <p className="mt-1 text-sm text-muted">{item.body}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="mt-8 rounded-xl border border-border bg-surface/60 p-6 text-center">
        <p className="text-base font-semibold text-foreground">Need help installing?</p>
        <p className="mt-2 text-sm text-muted">
          Our security team can walk you through the installation — free for your first session.
        </p>
        <a
          href="mailto:hello@lognsec.com?subject=Agent%20Installation%20Help"
          className="mt-4 inline-block rounded-lg border border-accent/50 bg-accent/10 px-5 py-2.5 text-sm font-semibold text-accent transition hover:bg-accent/20"
        >
          Email us for help →
        </a>
        <p className="mt-3 text-xs text-muted">We typically respond within a few hours.</p>
      </div>

      <section className="rounded-2xl border border-border bg-surface/40 p-5 text-center sm:p-6">
        <p className="text-sm text-muted">
          Already installed? Your device appears automatically in the Network page.
        </p>
        <Link
          to="/network"
          className="mt-3 inline-block text-sm font-semibold text-accent hover:underline"
        >
          Go to Network Monitor →
        </Link>
      </section>
    </div>
  );
}
